import type { KeyboardEvent } from 'react';

import type { KpiDataRange } from 'components/molecules/PackArchitecture/kpiAxisConfiguration.utils';
import { KPI_INTEGER_CONTROL_KEYS } from 'constants/ppa';

export const KPI_RANGE_INPUT_CLASS =
    '!h-6 !w-[68px] !rounded [&_.ant-input-number]:!h-6 [&_.ant-input-number]:!rounded [&_.ant-input-number-input-wrap]:!h-6 [&_.ant-input-number-input]:!h-6 [&_.ant-input-number-input]:!px-[7px]';

/** PA-07: range/bin inputs accept whole numbers only. */
export const KPI_INTEGER_INPUT_PROPS = {
    precision: 0,
    step: 1,
    inputMode: 'numeric' as const,
};

export const handleKpiIntegerInputKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (KPI_INTEGER_CONTROL_KEYS.has(event.key)) return;
    if ((event.ctrlKey || event.metaKey) && ['a', 'c', 'v', 'x', 'z'].includes(event.key.toLowerCase())) {
        return;
    }
    if (!/^\d$/.test(event.key)) {
        event.preventDefault();
    }
};

export const parseKpiIntegerInput = (value: number | string | null | undefined): number | null => {
    if (value == null || value === '') return null;
    const numeric = typeof value === 'number' ? value : Number(String(value));
    if (!Number.isFinite(numeric)) return null;
    return Math.round(numeric);
};

export const getTrackPercent = (value: number, dataRange: KpiDataRange) => {
    const span = dataRange.defaultMax - dataRange.defaultMin;
    if (span <= 0) return 0;
    return ((value - dataRange.defaultMin) / span) * 100;
};

export const getValueFromPercent = (percent: number, dataRange: KpiDataRange) => {
    const span = dataRange.defaultMax - dataRange.defaultMin;
    return Math.round(dataRange.defaultMin + (percent / 100) * span);
};

export const buildAxisTicks = (dataRange: KpiDataRange): number[] => {
    const span = dataRange.defaultMax - dataRange.defaultMin;
    const step = span <= 50 ? 5 : 10;
    const ticks: number[] = [];
    for (let v = dataRange.defaultMin + step; v < dataRange.defaultMax; v += step) {
        ticks.push(v);
    }
    return ticks;
};

export const getSliderLayout = (
    dataRange: KpiDataRange,
    min: number,
    max: number,
    binBreakpoints: number[],
    customBinning = false,
) => {
    const minPercent = getTrackPercent(min, dataRange);
    const maxPercent = getTrackPercent(max, dataRange);
    const isFullRange = min === dataRange.defaultMin && max === dataRange.defaultMax;
    const innerBreakpoints = binBreakpoints.filter((bp) => bp > min && bp < max);

    if (customBinning) {
        return {
            minPercent,
            maxPercent,
            isFullRange: true,
            aboveLabels: innerBreakpoints,
            dividers: innerBreakpoints,
        };
    }

    // Uniform binning — slider ticks/dividers follow computed bin breakpoints (count/size).
    return {
        minPercent,
        maxPercent,
        isFullRange,
        aboveLabels: innerBreakpoints,
        dividers: innerBreakpoints,
    };
};
